import { useCallback, useEffect, useState } from 'react';

const PANEL_STORAGE_PREFIX = 'embedChatPanel:';

function readStoredMode(storageKey) {
  try {
    return localStorage.getItem(storageKey) === 'expanded';
  } catch {
    // A visitor with storage disabled just starts at the default size every time.
    return false;
  }
}

/**
 * Everything the embed widget pushes OUT to the host page, plus the panel size it asks the
 * host to apply (EmbedChat.jsx). Split out of the page for the same reason useEmbedChat is:
 * the page should read as markup, not as a stack of postMessage effects.
 *
 * The size is persisted on the IFRAME side. embed.js is a cached static file on a
 * customer's site that we cannot force to update, so it holds as little state as possible —
 * it is told the mode again on every handshake instead of remembering one.
 */
export function useHostSync({
  key, agentKey, branding, sessionId, lastError, allowExpand,
  hasFeature, hostMobile, requestResize, publishSessionId, publishError, publishBranding,
}) {
  const storageKey = `${PANEL_STORAGE_PREFIX}${key}:${agentKey}`;
  const [expanded, setExpanded] = useState(() => readStoredMode(storageKey));

  // Without these two the host SDK is silently inert: AgentWidget.getSession() stays null
  // and an integrator's on('error') handler never fires, however badly things go.
  useEffect(() => {
    if (sessionId) publishSessionId(sessionId);
  }, [sessionId, publishSessionId]);

  useEffect(() => {
    if (lastError) publishError(lastError.message, lastError.code);
  }, [lastError, publishError]);

  // The launcher lives outside the iframe and is painted before it exists, so the host can
  // only learn the key's brand from here — it must not re-hit /resolve for it (that
  // endpoint shares the chat rate bucket).
  useEffect(() => {
    if (!branding || !hasFeature('branding')) return;
    publishBranding({
      color: branding.color,
      launcherIconUrl: branding.launcher_icon_url,
      position: branding.position,
    });
  }, [branding, hasFeature, publishBranding]);

  const canExpand = hasFeature('resize') && !hostMobile && allowExpand;

  // Re-sent whenever the host re-announces itself, so reopening the widget — or rotating a
  // device back to desktop — restores the size the visitor last chose.
  useEffect(() => {
    if (!canExpand) return;
    requestResize(expanded ? 'expanded' : 'default');
  }, [canExpand, expanded, requestResize]);

  const toggleExpand = useCallback(() => {
    setExpanded((current) => {
      const next = !current;
      try { localStorage.setItem(storageKey, next ? 'expanded' : 'default'); } catch { /* storage unavailable */ }
      return next;
    });
  }, [storageKey]);

  return { expanded, canExpand, toggleExpand };
}

export default useHostSync;
